import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { useMayPlan } from '../../hooks/useModules.js';
import {
  AMOUNT_UNITS,
  SHELF_CATEGORIES,
  itemsForShop,
  rememberedShop,
  shopShelves,
} from '@kraftwerk/shared';
import type { AmountUnit, ShelfCategory } from '@kraftwerk/shared';
import { db } from '../../db/local.js';
import type { LocalFood, LocalPantryItem, LocalShop, LocalShoppingItem } from '../../db/local.js';
import { listShops } from '../../db/shops.js';
import { ShopChips, ShopManager, TripPlanner } from './Shops.js';
import { listFoods } from '../../db/nutrition.js';
import {
  addPantryItem,
  addShoppingItem,
  currentHouseholdId,
  finishShopping,
  listPantry,
  listShopping,
  removePantryItem,
  removeShoppingItem,
  restoreShoppingItem,
  setChecked,
  setPantryAmount,
} from '../../db/shopping.js';
import type { ItemDraft } from '../../db/shopping.js';
import { toNumber } from '../nutrition/FoodForm.js';
import { HouseholdPanel } from './HouseholdPanel.js';
import { LocationPanel } from './Location.js';
import { CostsView } from './Costs.js';
import { PocketLedger } from './Pocket.js';
import { currentOwnerId } from '../../auth.js';
import { ShoppingCostPrompt } from './ShoppingCostPrompt.js';
import { PlanToList } from './PlanToList.js';
import { ConsumptionSuggestions } from './Consumption.js';
import { SwipePages, SwipeRow, neighbourIn, useUndo } from '../../gestures/SwipeProvider.js';
import { SealedNotice } from '../SealedNotice.js';

export type ShoppingView = 'list' | 'pantry' | 'costs' | 'pocket' | 'members';
type View = ShoppingView;

/**
 * Der Reiter „Haushalt“ (E15): Einkauf, Vorrat, Mitglieder — und Kosten (E17).
 *
 * Die Liste ist die Startansicht — sie wird im Laden gebraucht, mit einer Hand
 * und ohne Netz. „Aus dem Wochenplan“ steht in der Liste selbst, damit vier
 * Unterreiter in 375 px passen.
 */
export function ShoppingScreen({
  onMembershipChanged,
  initialView,
  initialShopId = null,
  nutrition = true,
  household = true,
  costs = true,
  pocket = false,
  onOpenFamilyDisplay,
  focusLocation = false,
  views,
}: {
  onMembershipChanged: () => void;
  /**
   * Räume (27.10.2026): Welche Unterreiter dieser Reiter zeigt — Einkauf
   * (Liste, Vorrat), Kosten, Taschengeld und Mitglieder sind im Raum
   * Haushalt eigene Reiter. Ohne Angabe: alle wie bisher. Bleibt nur einer,
   * entfällt die Unterleiste.
   */
  views?: readonly View[] | undefined;
  /** Aus einer Standort-Mitteilung geöffnet (27.10.2026). */
  focusLocation?: boolean;
  /** Familienanzeige (27.10.2026) öffnen — steht unter „Mitglieder“. */
  onOpenFamilyDisplay?: (() => void) | undefined;
  initialView?: View | undefined;
  /** Vom Einkaufstag auf „Heute“: gleich die Liste dieses Ladens. */
  initialShopId?: string | null | undefined;
  /** Wochenplan gibt es nur mit dem Modul Ernährung. */
  nutrition?: boolean;
  /** Module (E15): Liste und Vorrat mit „Haushalt“, Kosten mit „Kosten“. */
  household?: boolean;
  costs?: boolean;
  /** Taschengeld (E20): der eigene Reiter eines Kindes. */
  pocket?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const allTabs: { key: View; label: string }[] = [
    ...(household
      ? [
          { key: 'list' as const, label: t('shopping.tabs.list') },
          { key: 'pantry' as const, label: t('shopping.tabs.pantry') },
        ]
      : []),
    ...(costs ? [{ key: 'costs' as const, label: t('shopping.tabs.costs') }] : []),
    ...(pocket ? [{ key: 'pocket' as const, label: t('shopping.tabs.pocket') }] : []),
    { key: 'members', label: t('shopping.tabs.members') },
  ];
  const tabs = views ? allTabs.filter((tab) => views.includes(tab.key)) : allTabs;
  const first = tabs[0]?.key ?? 'members';
  const [view, setView] = useState<View>(
    initialView && tabs.some((tab) => tab.key === initialView) ? initialView : first,
  );
  // Kleine Kinder (E20) sehen Liste und Vorrat, schreiben aber nicht hinein.
  const mayPlan = useMayPlan();
  // Die eigene Mitgliedschaft — für das Taschengeld des Kindes.
  const ownerId = currentOwnerId();
  const me = useLiveQuery(
    async () =>
      pocket && ownerId
        ? ((await db.household_members.where('userId').equals(ownerId).toArray()).find(
            (member) => !member.deletedAt,
          ) ?? null)
        : null,
    [pocket, ownerId],
    null,
  );
  const householdId = useLiveQuery(() => currentHouseholdId(db), [], undefined);

  return (
    <div className="flex flex-col gap-3">
      {tabs.length > 1 ? (
        <nav aria-label={t('shopping.tabs.label')}>
          <ul className="flex gap-1 rounded-full border border-line bg-surface p-1">
            {tabs.map((tab) => (
              <li key={tab.key} className="flex-1">
                <button
                  type="button"
                  aria-current={view === tab.key ? 'page' : undefined}
                  onClick={() => setView(tab.key)}
                  className={`kw-btn kw-btn-small w-full px-1 ${
                    view === tab.key ? '' : 'border-0 bg-transparent text-ink-muted'
                  }`}
                >
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      ) : null}
      <SealedNotice id="shopping-sealed" />

      {householdId === null ? (
        // Vor dem ersten Abgleich gibt es keinen Haushalt — und damit keinen Ort,
        // an den ein Eintrag gehören könnte. Das wird gesagt, nicht überspielt.
        <p role="status" className="kw-tile text-sm text-warn">
          {t('shopping.noHousehold')}
        </p>
      ) : null}

      {/* Wischen auf dem Inhalt wechselt die Unterreiter (24.10.2026). */}
      <SwipePages
        current={view}
        neighbour={neighbourIn(tabs.map((tab) => tab.key))}
        onChange={setView}
        render={(key) => (
          <>
            {!mayPlan && (key === 'list' || key === 'pantry') ? (
              <p role="status" className="text-sm text-ink-muted">
                {t('children.level.readOnly')}
              </p>
            ) : null}
            {key === 'list' || key === 'pantry' ? (
              <fieldset
                disabled={!mayPlan}
                className="m-0 flex min-w-0 flex-col gap-3 border-0 p-0"
              >
                {key === 'list' ? (
                  <ShoppingList
                    householdId={householdId ?? null}
                    withPlan={nutrition}
                    withCosts={costs}
                    initialShopId={initialShopId}
                  />
                ) : (
                  <Pantry householdId={householdId ?? null} />
                )}
              </fieldset>
            ) : null}
            {key === 'costs' ? <CostsView householdId={householdId ?? null} /> : null}
            {key === 'pocket' && me ? (
              <PocketLedger child={me} mode="child" mayWrite={mayPlan} />
            ) : null}
            {key === 'members' ? (
              <div className="flex flex-col gap-3">
                {household ? (
                  <LocationPanel householdId={householdId ?? null} focus={focusLocation} />
                ) : null}
                <HouseholdPanel
                  householdId={householdId ?? null}
                  onChanged={onMembershipChanged}
                  onOpenFamilyDisplay={onOpenFamilyDisplay}
                />
              </div>
            ) : null}
          </>
        )}
      />
    </div>
  );
}

// --- Liste ---------------------------------------------------------------------

function ShoppingList({
  householdId,
  withPlan,
  withCosts,
  initialShopId,
}: {
  householdId: string | null;
  withPlan: boolean;
  /** Kosten (E17): nach „Einkauf abschließen“ nach dem Betrag fragen. */
  withCosts: boolean;
  initialShopId: string | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [fromPlan, setFromPlan] = useState(false);
  const items = useLiveQuery(
    () => listShopping(householdId, db),
    [householdId],
    [] as LocalShoppingItem[],
  );
  // Läden (18.10.2026): eine Liste, gefiltert nach Laden, in seiner Regalfolge.
  const shops = useLiveQuery(() => listShops(householdId, db), [householdId], [] as LocalShop[]);
  const history = useLiveQuery(
    async () =>
      householdId ? db.shopping_items.where('householdId').equals(householdId).toArray() : [],
    [householdId],
    [] as LocalShoppingItem[],
  );
  const [shopId, setShopId] = useState<string | null>(initialShopId);
  const liveShops = new Set(shops.map((shop) => shop.id));
  const selected = shopId && liveShops.has(shopId) ? shopId : null;
  const shop = shops.find((entry) => entry.id === selected) ?? null;
  const shopName = new Map(shops.map((entry) => [entry.id, entry.name]));
  const allOpen = items.filter((item) => !item.checkedAt);
  const counts = new Map<string | null, number>([
    [null, allOpen.length],
    ...shops.map((entry) => [entry.id, itemsForShop(allOpen, entry.id, liveShops).length] as const),
  ]);
  const open = itemsForShop(allOpen, selected, liveShops);
  const done = items.filter((item) => item.checkedAt);
  const [finished, setFinished] = useState<number | null>(null);

  return (
    <section aria-labelledby="shopping-heading" className="flex flex-col gap-3">
      {/* Die große Zahl nur, wenn es etwas zu zählen gibt — „0 offen“ in
          Riesenschrift war Platz ohne Aussage. */}
      {open.length > 0 ? (
        <div className="kw-hero">
          <h2 id="shopping-heading" className="kw-kicker">
            {t('shopping.list.heading')}
          </h2>
          <p className="mt-2 flex items-baseline gap-2">
            <span className="kw-figure text-6xl">{open.length}</span>
            <span className="text-sm text-ink-muted">{t('shopping.list.open')}</span>
          </p>
        </div>
      ) : (
        <h2 id="shopping-heading" className="sr-only">
          {t('shopping.list.heading')}
        </h2>
      )}

      <ShopChips shops={shops} selected={selected} counts={counts} onSelect={setShopId} />

      <ItemForm
        submitLabel={t('shopping.list.add')}
        shops={shops}
        defaultShopId={selected}
        rememberShop={(name) => rememberedShop(name, history, liveShops)}
        onSubmit={(draft) => addShoppingItem(draft, db).then(() => undefined)}
      />

      {withPlan ? (
        fromPlan ? (
          <PlanToList householdId={householdId} onAdded={() => setFromPlan(false)} />
        ) : (
          <button
            type="button"
            onClick={() => setFromPlan(true)}
            className="kw-btn kw-btn-quiet kw-btn-small"
          >
            {t('shopping.fromPlan')}
          </button>
        )
      ) : null}

      {/* Beobachteter Verbrauch (27.10.2026) — nur, wenn etwas dran ist. */}
      <ConsumptionSuggestions householdId={householdId} />

      {open.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('shopping.list.empty')}</p>
      ) : (
        <GroupedByShelf
          items={open}
          order={shop?.shelfOrder ?? null}
          render={(item) => (
            <ShoppingRow
              key={item.id}
              item={item}
              shopName={
                selected === null && item.shopId ? (shopName.get(item.shopId) ?? null) : null
              }
            />
          )}
        />
      )}

      {done.length > 0 ? (
        <div className="kw-tile flex flex-col gap-2">
          <h3 className="kw-label">{t('shopping.list.done', { count: done.length })}</h3>
          <ul className="flex flex-col gap-1">
            {done.map((item) => (
              <ShoppingRow key={item.id} item={item} />
            ))}
          </ul>
          <button
            type="button"
            className="kw-btn"
            onClick={() => void finishShopping(db).then(setFinished)}
          >
            {t('shopping.list.finish', { count: done.length })}
          </button>
          <p className="text-xs text-ink-muted">{t('shopping.list.finishHint')}</p>
        </div>
      ) : null}

      {finished !== null ? (
        <p role="status" className="text-sm text-good">
          {t('shopping.list.finished', { count: finished })}
        </p>
      ) : null}
      {finished !== null && withCosts ? (
        <ShoppingCostPrompt householdId={householdId} onDone={() => setFinished(null)} />
      ) : null}

      <TripPlanner householdId={householdId} shops={shops} />
      <ShopManager householdId={householdId} shops={shops} />
    </section>
  );
}

function ShoppingRow({
  item,
  shopName = null,
}: {
  item: LocalShoppingItem;
  /** In „Alle“: bei welchem Laden. */
  shopName?: string | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const checked = item.checkedAt !== null;
  const mayPlan = useMayPlan();
  const showUndo = useUndo();
  const remove = () => {
    void removeShoppingItem(item.id, db).then(() =>
      showUndo(
        t('gestures.deleted', { name: item.name }),
        () => void restoreShoppingItem(item, db),
      ),
    );
  };

  // Wischen (24.10.2026): nach rechts abhaken, nach links löschen mit
  // „Rückgängig“. Häkchen und × bleiben der eigentliche Weg.
  return (
    <SwipeRow
      className="flex items-center gap-2"
      right={
        mayPlan
          ? {
              label: checked ? t('gestures.uncheck') : t('gestures.check'),
              tone: 'good',
              run: () => void setChecked(item.id, !checked, db),
            }
          : null
      }
      left={mayPlan ? { label: t('gestures.delete'), tone: 'danger', run: remove } : null}
    >
      {/* Ein echtes Kontrollkästchen in einer 44-px-Fläche: Vorleseprogramme
          sagen „abgehakt", und der Daumen trifft es auch im Gehen. */}
      <label className="flex min-h-11 min-w-0 flex-1 cursor-pointer items-center gap-3">
        <input
          type="checkbox"
          checked={checked}
          onChange={(event) => void setChecked(item.id, event.target.checked, db)}
          className="size-6 shrink-0 accent-good"
        />
        <span className={`min-w-0 ${checked ? 'text-ink-muted line-through' : ''}`}>
          <span className="block truncate">{item.name}</span>
          <span className="block text-xs text-ink-muted">
            <Amount amount={item.amount} unit={item.unit} />
            {item.fromPlan ? ` · ${t('shopping.list.fromPlan')}` : ''}
            {shopName ? ` · ${shopName}` : ''}
          </span>
        </span>
      </label>
      <button
        type="button"
        onClick={remove}
        aria-label={t('shopping.list.remove', { name: item.name })}
        className="kw-btn kw-btn-quiet kw-btn-small shrink-0 px-3 hover:border-danger hover:text-danger"
      >
        ×
      </button>
    </SwipeRow>
  );
}

// --- Vorrat --------------------------------------------------------------------

function Pantry({ householdId }: { householdId: string | null }): React.JSX.Element {
  const { t } = useTranslation();
  const items = useLiveQuery(
    () => listPantry(householdId, db),
    [householdId],
    [] as LocalPantryItem[],
  );

  return (
    <section aria-labelledby="pantry-heading" className="flex flex-col gap-3">
      <div>
        <h2 id="pantry-heading" className="text-lg font-semibold">
          {t('shopping.pantry.heading')}
        </h2>
        <p className="text-sm text-ink-muted">{t('shopping.pantry.intro')}</p>
      </div>

      <ItemForm
        submitLabel={t('shopping.pantry.add')}
        onSubmit={(draft) => addPantryItem(draft, db).then(() => undefined)}
      />

      {items.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('shopping.pantry.empty')}</p>
      ) : (
        <GroupedByShelf items={items} render={(item) => <PantryRow key={item.id} item={item} />} />
      )}
    </section>
  );
}

function PantryRow({ item }: { item: LocalPantryItem }): React.JSX.Element {
  const { t } = useTranslation();
  const amountId = useId();
  const [draft, setDraft] = useState(item.amount?.toString() ?? '');

  const save = () => {
    const amount = toNumber(draft);
    if (amount === item.amount) return;
    void setPantryAmount(item.id, amount, item.unit ?? 'g', db);
  };

  return (
    <li className="flex items-center gap-2">
      <span className="min-w-0 flex-1">
        <span className="block truncate">{item.name}</span>
        <span className="block text-xs text-ink-muted">
          <Amount amount={item.amount} unit={item.unit} />
        </span>
      </span>
      <label htmlFor={amountId} className="sr-only">
        {t('shopping.pantry.amountFor', { name: item.name })}
      </label>
      <input
        id={amountId}
        inputMode="decimal"
        value={draft}
        placeholder={t('shopping.amountUnknownShort')}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={save}
        className="kw-field w-24 text-ink"
      />
      <button
        type="button"
        onClick={() => void removePantryItem(item.id, db)}
        aria-label={t('shopping.pantry.remove', { name: item.name })}
        className="kw-btn kw-btn-quiet kw-btn-small shrink-0 px-3 hover:border-danger hover:text-danger"
      >
        ×
      </button>
    </li>
  );
}

// --- Bausteine -----------------------------------------------------------------

/** Menge oder ausdrücklich „Menge offen" — nie eine stumme Lücke. */
export function Amount({
  amount,
  unit,
}: {
  amount: number | null;
  unit: AmountUnit | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  if (amount === null) return <>{t('shopping.amountUnknown')}</>;
  return <>{t(`shopping.unitFormat.${unit ?? 'g'}`, { amount: Math.round(amount * 10) / 10 })}</>;
}

function GroupedByShelf<T extends { id: string; category: ShelfCategory }>({
  items,
  order = null,
  render,
}: {
  items: T[];
  /** Regalfolge des gewählten Ladens; `null` = die übliche. */
  order?: string[] | null;
  render: (item: T) => React.JSX.Element;
}): React.JSX.Element {
  const { t } = useTranslation();
  const groups = shopShelves(order)
    .map((category) => ({
      category,
      items: items.filter((item) => item.category === category),
    }))
    .filter((group) => group.items.length > 0);

  return (
    <div className="flex flex-col gap-3">
      {groups.map((group) => (
        <section key={group.category} className="kw-tile">
          <h3 className="kw-label">{t(`shopping.category.${group.category}`)}</h3>
          <ul className="mt-1 flex flex-col gap-1">{group.items.map(render)}</ul>
        </section>
      ))}
    </div>
  );
}

function ItemForm({
  submitLabel,
  onSubmit,
  shops = [],
  defaultShopId = null,
  rememberShop,
}: {
  submitLabel: string;
  onSubmit: (draft: ItemDraft) => Promise<void>;
  /** Nur auf der Einkaufsliste: Läden zur Auswahl. */
  shops?: LocalShop[];
  defaultShopId?: string | null;
  /** Der Laden, in dem man das zuletzt gekauft hat. */
  rememberShop?: (name: string) => string | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const nameId = useId();
  const libraryId = useId();
  const amountId = useId();
  const unitId = useId();
  const categoryId = useId();
  const shopFieldId = useId();
  const [name, setName] = useState('');
  // `undefined`: nicht selbst gewählt — dann gilt der gemerkte oder der gefilterte Laden.
  const [shopChoice, setShopChoice] = useState<string | null | undefined>(undefined);
  const shopFor = (value: string) =>
    shopChoice !== undefined ? shopChoice : (rememberShop?.(value) ?? defaultShopId ?? null);
  const [amount, setAmount] = useState('');
  const [unit, setUnit] = useState<AmountUnit>('g');
  const [category, setCategory] = useState<ShelfCategory>('other');
  // Meistens reicht der Name: „Milch“, Enter, fertig. Menge und Regal gibt es
  // auf Wunsch — und offen bleibt es, sobald man es einmal braucht.
  const [details, setDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Aus der Bibliothek: übernimmt Name und Regal — eingefroren, kein Verweis,
  // denn Liste und Vorrat teilt der Haushalt, die Bibliothek nicht.
  const foods = useLiveQuery(() => listFoods(db), [], [] as LocalFood[]);

  return (
    <form
      className="kw-tile flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (name.trim() === '') return;
        setError(null);
        const draft = {
          name,
          amount: toNumber(amount),
          unit,
          category,
          ...(shops.length > 0 ? { shopId: shopFor(name) } : {}),
        };
        // Sofort leeren — schnell Getipptes hängt sonst am vorigen Namen.
        setName('');
        setAmount('');
        setShopChoice(undefined);
        onSubmit(draft).catch(() => {
          setName(draft.name);
          setError(t('shopping.noHousehold'));
        });
      }}
    >
      <label htmlFor={nameId} className="kw-label">
        {t('shopping.item.name')}
      </label>
      <div className="flex gap-2">
        <input
          id={nameId}
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
          maxLength={120}
          enterKeyHint="done"
          className="kw-field min-w-0 flex-1"
        />
        <button type="submit" className="kw-btn kw-btn-small shrink-0 px-4">
          {submitLabel}
        </button>
      </div>
      {foods.length > 0 ? (
        <label htmlFor={libraryId} className="flex flex-col gap-1">
          <span className="kw-label">{t('shopping.item.fromLibrary')}</span>
          <select
            id={libraryId}
            value=""
            onChange={(event) => {
              const food = foods.find((entry) => entry.id === event.target.value);
              if (!food) return;
              setName(food.name);
              setCategory(food.category ?? 'other');
            }}
            className="kw-field"
          >
            <option value="">{t('nutrition.day.choose')}</option>
            {foods.map((food) => (
              <option key={food.id} value={food.id}>
                {food.brand ? `${food.name} · ${food.brand}` : food.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {shops.length > 0 ? (
        <label htmlFor={shopFieldId} className="flex flex-col gap-1">
          <span className="kw-label">{t('shops.shop')}</span>
          <select
            id={shopFieldId}
            value={shopFor(name) ?? ''}
            onChange={(event) => setShopChoice(event.target.value || null)}
            className="kw-field"
          >
            <option value="">{t('shops.anywhere')}</option>
            {shops.map((shop) => (
              <option key={shop.id} value={shop.id}>
                {shop.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {details ? (
        <div className="flex flex-wrap gap-2">
          <label htmlFor={amountId} className="flex w-20 flex-col gap-1">
            <span className="kw-label">{t('shopping.item.amount')}</span>
            <input
              id={amountId}
              inputMode="decimal"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              className="kw-field"
            />
          </label>
          <label htmlFor={unitId} className="flex w-24 flex-col gap-1">
            <span className="kw-label">{t('shopping.item.unit')}</span>
            <select
              id={unitId}
              value={unit}
              onChange={(event) => setUnit(event.target.value as AmountUnit)}
              className="kw-field"
            >
              {AMOUNT_UNITS.map((option) => (
                <option key={option} value={option}>
                  {t(`shopping.unit.${option}`)}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor={categoryId} className="flex min-w-32 flex-1 flex-col gap-1">
            <span className="kw-label">{t('shopping.item.category')}</span>
            <select
              id={categoryId}
              value={category}
              onChange={(event) => setCategory(event.target.value as ShelfCategory)}
              className="kw-field"
            >
              {SHELF_CATEGORIES.map((option) => (
                <option key={option} value={option}>
                  {t(`shopping.category.${option}`)}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setDetails(true)}
          className="self-start py-2 text-sm text-ink-muted underline underline-offset-2"
        >
          {t('shopping.item.details')}
        </button>
      )}
      {error ? (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : null}
    </form>
  );
}
